/* import __COLOCATED_TEMPLATE__ from './drill-in-card-view.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { restartableTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type Range from 'embercom/models/reporting/range';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
import { isEmpty, isPresent } from '@ember/utils';
import { buildFiltersForDataConfig } from 'embercom/lib/reporting/custom/data-config-builder-helpers';
import type RenderableChart from 'embercom/models/reporting/custom/renderable-chart';
import type { DrillInViewType } from 'embercom/components/reporting/custom/report/underlying-data-modal';

interface Args {
  dateRange: Range;
  chartSeries: ChartSeries;
  renderableChart: RenderableChart;
  filters: LogicalFilter;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
  pageFrom: number;
  updatePageFrom: (value: number) => void;
  resetPageFrom: () => void;
  isStandalone: boolean;
  view: DrillInViewType;
  availableViews: DrillInViewType[];
  setView: (view: DrillInViewType) => void;
}

export interface DrillInConversationCard {
  id: number;
  userName: string;
  userPseudonym: string;
  cxExplanation: string;
  cxScore: number;
  cxAttributes: string;
  topicSummary: string;
}

const CONVERSATION_PAGE_SIZE = 25;

export default class DrillInCardView extends Component<Args> {
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare appService: AppService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare intl: IntlService;
  @service declare reportingChartService: ReportingChartService;
  @tracked selectedConversationId = 0;
  @tracked totalHits = 0;
  @tracked loadedConversations: DrillInConversationCard[] = [];
  fetchDataTask = taskFor(this.loadConversationCardData);

  conversationData = trackedTask(this, this.fetchDataTask, () => [
    this.args.dateRange,
    this.filters,
    this.args.pageFrom,
  ]);

  get metric() {
    return this.args.chartSeries.metric;
  }

  get isInitialLoad() {
    return this.conversationData.isRunning && isEmpty(this.loadedConversations);
  }

  @restartableTask
  *loadConversationCardData(): TaskGenerator<void> {
    if (this.args.pageFrom === 0) {
      this.loadedConversations = [];
    }

    let timeRange: FlexibleQueryTimeRange = {
      start: this.args.dateRange.startMoment.valueOf(),
      end: this.args.dateRange.endMoment.valueOf(),
      interval: this.args.dateRange.interval,
      property: this.metric.timeProperty,
    };
    let attributeIds = [
      'conversation.id',
      'conversation.first_user.name',
      'conversation.first_user.pseudonym',
      'conversation_custom_fields#ai_insights_conversation_summary',
      'conversation.ai_generated_metrics.csat_explanation',
      'conversation.ai_generated_metrics.csat',
    ];
    if (this.appService.app.useNewCxSummary) {
      attributeIds.push('conversation_custom_fields#customer_experience_summary');
    }
    if (this.appService.app.canSeeCxV2) {
      attributeIds.push('conversation_custom_fields#customer_experience_attributes');
    }
    let sortBy = 'conversation.first_user_conversation_part_created_at';
    let sortDirection: 'asc' | 'desc' = 'desc';
    let syntheticDataUseCase = this.reportingChartService.syntheticDataUseCase;
    let isBeingRenderedAsAChart = false;
    let pageSize = CONVERSATION_PAGE_SIZE;
    let result = yield this.reportingUnderlyingDataService.loadDrillInResult(
      this.appService.app.id,
      this.metric.datasetId,
      this.filters as LogicalFilter,
      this.args.pageFrom,
      pageSize,
      timeRange,
      attributeIds,
      sortBy,
      sortDirection,
      syntheticDataUseCase,
      this.args.dateRange.timezone,
      isBeingRenderedAsAChart,
    );
    if (isPresent(result.data)) {
      this.onConversationSelected(result.data[0]['conversation.id']);
    }
    this.totalHits = result.totalHits;
    this.loadedConversations.pushObjects(
      result.data.map(
        (conversation: any) =>
          ({
            id: conversation['conversation.id'],
            userName: conversation['conversation.first_user.name'],
            userPseudonym: conversation['conversation.first_user.pseudonym'],
            cxExplanation: this.appService.app.useNewCxSummary
              ? conversation['conversation_custom_fields#customer_experience_summary'] ||
                conversation['conversation.ai_generated_metrics.csat_explanation']
              : conversation['conversation.ai_generated_metrics.csat_explanation'],
            cxScore: conversation['conversation.ai_generated_metrics.csat'],
            cxAttributes: conversation['conversation_custom_fields#customer_experience_attributes'],
            topicSummary:
              conversation['conversation_custom_fields#ai_insights_conversation_summary'],
          }) as DrillInConversationCard,
      ),
    );
  }

  get filters() {
    return buildFiltersForDataConfig(
      this.args.renderableChart,
      this.metric,
      undefined,
      this.args.filters,
      false,
      this.metric.datasetId !== 'admin_status_change',
    );
  }

  get conversationIds() {
    return this.loadedConversations.map((conversation) => conversation.id);
  }

  get hasMoreConversations() {
    return this.loadedConversations.length < this.totalHits;
  }

  get selectedConversationCXExplanation() {
    return this.loadedConversations.find(
      (conversation) => conversation.id === this.selectedConversationId,
    )?.cxExplanation;
  }

  get selectedConversationCXScore() {
    return this.loadedConversations.find(
      (conversation) => conversation.id === this.selectedConversationId,
    )?.cxScore;
  }

  get selectedConversationCXAttributes() {
    return this.loadedConversations.find(
      (conversation) => conversation.id === this.selectedConversationId,
    )?.cxAttributes;
  }

  @action
  loadMoreConversations() {
    this.args.updatePageFrom(1); // add one page
    this.args.trackAnalyticsEvent('load-more-conversations', 'conversation-explorer', {
      pageFrom: this.args.pageFrom,
    });
  }

  @action
  onConversationSelected(conversationId: number) {
    this.args.trackAnalyticsEvent('conversation-card-click', 'drill-in-card-view', {
      conversationId,
    });
    this.selectedConversationId = conversationId;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::DrillInCardView': typeof DrillInCardView;
  }
}
